import {Alignment, Border, Cell, Column, Fill, Font, PaperSize, Workbook, Worksheet} from 'exceljs';
import {COLORED_BORDERS, ExcelBordersHelper} from './excel-borders.helper';
import {ExcelTab, ExcelTable, ExcelTableColumn, ExcelTableRow} from './excel.interfaces';
import {
	WORKSHEET_CELL_LEFT_INDENT,
	WORKSHEET_COLUMN_HEADERS_ROW_ID,
	WORKSHEET_COLUMN_HEADERS_STYLES,
	WORKSHEET_COLUMN_WIDTH,
	WORKSHEET_CONTENT_ROW_ID,
	WORKSHEET_HEADER_ROW_ID,
	WORKSHEET_HEADER_STYLES,
	WORKSHEET_SUB_HEADER_STYLES,
	WORKSHEET_TOTAL_CELL_STYLES,
} from './excel.constants';

/** Auxiliary 'ALPHABET' variable.' */
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * 'ExcelCommonHelper' class definition.
 */
export class ExcelCommonHelper {
	/** Auxiliary 'NUMBER_FORMATTER' variable. */
	public static NUMBER_FORMATTER = '#,##0_);[RED](#,##0)';

	/**
	 * Removes inapplicable symbols from a possible filename
	 * @param {string} name
	 * @returns {string}
	 */
	public static escapeFilename(name: string): string {
		return name.replace(/\//g, '_');
	}

	/**
	 * Get Column Letter By Id
	 * @param id - starting from 1
	 * @returns {string}
	 */
	public static getColumnLetterById(id: number): string {
		const letterIndex = (id - 1) % ALPHABET.length;

		if (id <= ALPHABET.length) {
			return ALPHABET[letterIndex];
		}

		const letterTimes = Math.floor((id - 1) / ALPHABET.length) - 1;
		const firstLetter = ALPHABET[letterTimes];
		const secondLetter = ALPHABET[letterIndex];
		return `${firstLetter}${secondLetter}`;
	}

	/**
	 * Creates worksheet with print area of given size (defined by bottom right cell id)
	 * @param {Workbook | any} wb
	 * @param {string} sheetLabel
	 * @param {string} printArea
	 * @returns {Worksheet}
	 */
	public static createWorksheet(wb: Workbook | any, sheetLabel: string, printArea?: string): Worksheet {
		const setPrintArea = printArea || 'M15';
		return wb.addWorksheet(sheetLabel, {
			pageSetup: {
				setPrintArea,
				fitToWidth: 1,
				fitToHeight: 1,
				paperSize: PaperSize.A4,
				orientation: 'portrait',
				horizontalCentered: true,
				verticalCentered: true,
				margins: {
					footer: 0.1,
					header: 0.1,
					bottom: 0.1,
					left: 0.1,
					right: 0.1,
					top: 0.1,
				},
			},
			views: [{zoomScale: 80}],
		}) as Worksheet;
	}

	/**
	 * Generates an Excel worksheet within a specified workbook.
	 * This method creates a worksheet with a title, headers, date range, table header, table content etc.
	 * based on the provided data and configuration.
	 * @param {Workbook} workbook
	 * @param {ExcelTab} tab
	 * @param {ExcelTableColumn[]} columnsConfig
	 * @returns {void}
	 */
	public static generateWorksheet(workbook: Workbook, tab: ExcelTab, columnsConfig: ExcelTableColumn[]): void {
		const tables: ExcelTable[] = tab.tables;
		const sheetTitle: string = tab.title;
		const sheetLabel: string = tab.label || sheetTitle;
		const columnsConfigs = ExcelCommonHelper.getExcelTableConfig(tables[0].rows[0], columnsConfig);

		// Define file's used cells
		const dimensions = this.defineSheetDimensions(tables[0], columnsConfigs, tab.widthInCells);
		const bottomRightCellId = `${dimensions.lastColumnLetter}${dimensions.lastRowId}`;
		// console.log('bottomRightCellId', bottomRightCellId);

		// Create first sheet
		const worksheet = ExcelCommonHelper.createWorksheet(workbook, sheetLabel, bottomRightCellId);

		// Adjust some print preferences
		worksheet.pageSetup.fitToHeight = 0;
		worksheet.pageSetup.verticalCentered = false;

		// Add content
		ExcelCommonHelper.addHeader(worksheet, dimensions.lastColumnLetter, sheetTitle);

		let rowsOffset = WORKSHEET_CONTENT_ROW_ID;
		tables.forEach((table) => {
			const columnsCnfgs: ExcelTableColumn[] =
				table.columnsConfig || ExcelCommonHelper.getExcelTableConfig(table.rows[0], columnsConfig);
			if (!table.customColumnHeader) {
				// addColumnHeaders moved inside addTableContent to allow MULTI tables
				ExcelCommonHelper.addColumnHeaders(worksheet, columnsCnfgs);
			}
			rowsOffset = ExcelCommonHelper.addTableContent(worksheet, table, columnsCnfgs, rowsOffset);

			// Set width to columns
			ExcelCommonHelper.applyColumnWidth(worksheet, columnsCnfgs);
		});
	}

	/**
	 * Defines the dimensions of an Excel worksheet based on the number of columns and rows.
	 * @param {ExcelTable} table
	 * @param {ExcelTableColumn[] | string[]} columnsConfigs
	 * @param tabWidthInCells
	 * @returns {void}
	 */
	// todo: add shared return type
	private static defineSheetDimensions(
		table: ExcelTable,
		columnsConfigs: ExcelTableColumn[] | string[],
		tabWidthInCells?
	) {
		const maxLength = Math.max(tabWidthInCells, table.columnsNumber, columnsConfigs?.length, 8);
		return {
			lastColumnLetter: ExcelCommonHelper.getColumnLetterById(maxLength),
			lastRowId: 1 + table.rows.length - 1,
		};
	}

	/**
	 * Populates the worksheet with table content using the provided data and column configurations.
	 * @param {Worksheet} worksheet
	 * @param {ExcelTable} table
	 * @param {ExcelTableColumn[]} columnsConfigs
	 * @param {number} rowsOffset
	 * @returns {number}
	 */
	private static addTableContent(
		worksheet: Worksheet,
		table: ExcelTable,
		columnsConfigs: ExcelTableColumn[],
		rowsOffset: number
	): number {
		const totalCellStyles = this.getSheetTotalCellStyles();
		const subHeaderCellStyles = this.getSheetSubHeaderCellStyles();
		const lastColumnLetter = ExcelCommonHelper.getColumnLetterById(table.columnsNumber);
		let rowsCounter = rowsOffset;

		table.rows.forEach((row, rowIndex: number) => {
			rowsCounter++;
			if (row === null) {
				// this is way to insert empty rows
				return;
			}
			if (row?.isColumnHeader === true) {
				if (!row?.isEmptyRow) {
					ExcelCommonHelper.addColumnHeaders(worksheet, columnsConfigs, rowsOffset + rowIndex);
				}
				return;
			}
			if (row?.isSubHeader === true || row?.isInlineHeader === true) {
				const cellAddress = `A${rowsOffset + rowIndex}`; // rowsOffset - starting raw with content
				const cell: Cell = worksheet.getCell(cellAddress);
				worksheet.mergeCells(`A${rowsOffset + rowIndex}:${lastColumnLetter}${rowsOffset + rowIndex}`);

				if (row.isSubHeader) {
					worksheet.getRow(rowsOffset + rowIndex).height = 32;
					cell.font = subHeaderCellStyles.font;
					cell.fill = subHeaderCellStyles.fill as Fill;
				}

				if (row.isInlineHeader) {
					cell.font = totalCellStyles.font;
					cell.fill = totalCellStyles.fill as Fill;
				}

				cell.value = String(row.title);
				return;
			}

			columnsConfigs.forEach((column, columnIndex: number) => {
				const columnId = ExcelCommonHelper.getColumnLetterById(columnIndex + 1);
				const cellAddress = `${columnId}${rowsOffset + rowIndex}`; // rowsOffset - starting raw with content
				const cell: Cell = worksheet.getCell(cellAddress);

				// Set value & styles
				cell.value = row[column.property];
				cell.border = ExcelBordersHelper.createBorders(COLORED_BORDERS.DARK_GREY);
				if (cell.value !== null && column.numFmt) {
					cell.numFmt = column.numFmt as string;
				} else if (cell.value !== null && column.formatter) {
					cell.numFmt = ExcelCommonHelper.NUMBER_FORMATTER;
				}

				if (column.excelAlign) {
					cell.alignment = {
						vertical: 'middle',
						horizontal: column.excelAlign,
						// wrapText: column.excelAlign === 'left',
						indent:
							column.excelAlign === 'center'
								? 0
								: column.excelAlign === 'right'
								? 0.4
								: WORKSHEET_CELL_LEFT_INDENT,
					};
				} else {
					cell.alignment = {indent: WORKSHEET_CELL_LEFT_INDENT};
				}

				const isOdd = rowIndex % 2 === 0;
				if (row.isTotal) {
					cell.font = totalCellStyles.font;
					cell.fill = totalCellStyles.fill as Fill;
				} else if (row.customFont) {
					cell.font = {...cell.font, ...(row.customFont as Font)};
				} else if (column.customFont) {
					cell.font = {...cell.font, ...column.customFont};
				} else if (isOdd) {
					cell.fill = {
						type: 'pattern',
						pattern: 'solid',
						fgColor: {argb: 'ffeeeeee'},
					};
					// cell.font = { bold: true };
				}

				if (row.customFill) {
					cell.fill = {...cell.fill, ...(row.customFill as Fill)};
				} else if (column.customFill) {
					cell.fill = {...cell.fill, ...column.customFill};
				}

				if (row.customBorder) {
					cell.border = {...cell.border, ...(row.customBorder as Partial<Border>)};
				} else if (column.customBorder) {
					cell.border = {...cell.border, ...column.customBorder};
				}

				// fallback null to emdash
				if (cell.value === null) {
					cell.value = '—';
				}
			});
		});

		return rowsCounter;
	}

	/**
	 * Adds a header to the worksheet with the specified title.
	 * @param {Worksheet} worksheet
	 * @param {string} lastColumnLetter
	 * @param {string} fileTitle
	 * @returns {void}
	 */
	private static addHeader(worksheet: Worksheet, lastColumnLetter: string, fileTitle?: string) {
		const styles = this.getSheetHeaderCellStyles();

		// Set height & make wider
		worksheet.getRow(WORKSHEET_HEADER_ROW_ID).height = 50;
		worksheet.mergeCells(`A${WORKSHEET_HEADER_ROW_ID}:${lastColumnLetter}${WORKSHEET_HEADER_ROW_ID}`);

		// Set value & styles
		const fileTitleCell = worksheet.getCell(`A${WORKSHEET_HEADER_ROW_ID}`);
		fileTitleCell.value = fileTitle || 'REPORT';
		fileTitleCell.font = styles.font;
		fileTitleCell.fill = styles.fill as Fill;
		fileTitleCell.alignment = styles.alignment as Alignment;
	}

	/**
	 * Add column headers.
	 * @param {Worksheet} worksheet
	 * @param {ExcelTableColumn[]} columnsConfigs
	 * @param {number} customRowId
	 * @returns {void}
	 */
	private static addColumnHeaders(worksheet: Worksheet, columnsConfigs: ExcelTableColumn[], customRowId?: number) {
		const styles = this.getSheetColumnHeadersStyles();

		// Set height & make wider
		worksheet.getRow(customRowId || WORKSHEET_COLUMN_HEADERS_ROW_ID).height = 40;

		let prevCell: Cell;
		for (let i = 0; i < columnsConfigs.length; i++) {
			const columnId = ExcelCommonHelper.getColumnLetterById(i + 1);
			const cellAddress = `${columnId}${customRowId || WORKSHEET_COLUMN_HEADERS_ROW_ID}`;
			const cell: Cell = worksheet.getCell(cellAddress);
			// Set value & styles
			cell.value = columnsConfigs[i]?.title || '';
			cell.font = styles.font;
			cell.fill = styles.fill as Fill;
			cell.alignment = styles.alignment as Alignment;
			if (i > 1 && columnsConfigs[i]?.mergeWithPrev && prevCell) {
				cell.merge(prevCell);
			}
			prevCell = cell;
		}
	}

	/**
	 * Get excel table config.
	 * @param {ExcelTableRow} row
	 * @param {ExcelTableColumn[]} configMap
	 * @returns {ExcelTableColumn[]}
	 */
	public static getExcelTableConfig(row: ExcelTableRow, configMap?: ExcelTableColumn[]): ExcelTableColumn[] {
		if (!row) {
			return [];
		}
		let filteredProps: ExcelTableColumn[] | string[] = [];
		if (configMap) {
			filteredProps = configMap.filter((columnConfig) =>
				Object.prototype.hasOwnProperty.call(row, columnConfig.property)
			);
		} else {
			filteredProps = Object.keys(row);
		}
		// console.log('filteredProps', configMap, row, filteredProps);
		return filteredProps as ExcelTableColumn[];
	}

	/**
	 * Applies the specified column widths and alignments to the worksheet columns.
	 * @param {Worksheet} worksheet
	 * @param {ExcelTableColumn[]} columnsConfigs
	 * @returns {void}
	 */
	private static applyColumnWidth(worksheet: Worksheet, columnsConfigs: ExcelTableColumn[]): void {
		for (let i = 0; i < columnsConfigs.length; i++) {
			worksheet.getColumn(i + 1).width =
				columnsConfigs[i]?.customWidth > 0 ? columnsConfigs[i]?.customWidth : WORKSHEET_COLUMN_WIDTH;
			if (columnsConfigs[i]?.customWidth === -1) {
				ExcelCommonHelper.adjustColumnWidth(worksheet.getColumn(i + 1));
			}
		}
	}

	/**
	 * Adjust column width.
	 * @param {Column} column
	 * @returns {void}
	 */
	private static adjustColumnWidth(column: Column) {
		const lengths = column.values.map((v) => v.toString().length);
		const maxLength = Math.max(...lengths.filter((v) => typeof v === 'number'));
		column.width = maxLength + 2;
	}

	/** Auxiliary 'getSheetHeaderCellStyles' arrow function. */
	private static getSheetHeaderCellStyles = () => WORKSHEET_HEADER_STYLES;

	/** Auxiliary 'getSheetSubHeaderCellStyles' arrow function. */
	private static getSheetSubHeaderCellStyles = () => WORKSHEET_SUB_HEADER_STYLES;

	/** Auxiliary 'getSheetColumnHeadersStyles' arrow function. */
	private static getSheetColumnHeadersStyles = () => WORKSHEET_COLUMN_HEADERS_STYLES;

	/** Auxiliary 'getSheetTotalCellStyles' arrow function. */
	private static getSheetTotalCellStyles = () => WORKSHEET_TOTAL_CELL_STYLES;
}
